import config from '../../config';
import { createRequestCache } from '../Page/HomePage/floorDeckRequestCache';

const dailyCache = createRequestCache(60 * 1000);
const monthlyCache = createRequestCache(60 * 1000);
const METRIC_STATUSES = new Set([
    'calculated',
    'no_planning',
    'no_data',
    'incomplete_planning',
]);
const METRIC_SOURCES = new Set([
    'dynamic',
    'snapshot',
    'aggregate',
    'legacy',
    'mixed',
]);

function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isNonEmptyString(value) {
    return typeof value === 'string' && value.length > 0;
}

function isNullableFiniteNumber(value) {
    return value === null || (typeof value === 'number' && Number.isFinite(value));
}

function isMetric(value) {
    return isObject(value)
        && METRIC_STATUSES.has(value.status)
        && isNullableFiniteNumber(value.oee)
        && isNullableFiniteNumber(value.availability)
        && METRIC_SOURCES.has(value.source);
}

function validateReport(report, periodKind) {
    if (!isObject(report) || report.formulaVersion !== 2 || report.periodKind !== periodKind
        || !Array.isArray(report.cells)
        || !report.cells.every((cell) => isMetric(cell)
            && isNonEmptyString(cell.machine)
            && isNonEmptyString(cell.period))
        || !Array.isArray(report.machineSummaries)
        || !report.machineSummaries.every((summary) => isMetric(summary)
            && isNonEmptyString(summary.machine))
        || !Array.isArray(report.periodSummaries)
        || !report.periodSummaries.every((summary) => isMetric(summary)
            && isNonEmptyString(summary.period))
        || !isMetric(report.overallSummary)) {
        throw new Error('Invalid OEE report response');
    }
    return report;
}

function requestReport(cache, key, url, body, periodKind) {
    const loader = () => fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    }).then(async (response) => {
        const text = await response.text();
        const data = text ? JSON.parse(text) : null;
        if (!response.ok) {
            const error = new Error((data && data.error) || 'Unable to load OEE report');
            error.retryable = data ? data.retryable !== false : true;
            throw error;
        }
        return validateReport(data, periodKind);
    });

    return cache.getStaleWhileRevalidate(key, loader);
}

function selectedMachineName(item) {
    if (typeof item === 'string') return item.trim() ? item : '';
    if (!item || typeof item !== 'object') return '';
    if (typeof item.value === 'string' && item.value.trim()) return item.value;
    if (typeof item.label === 'string' && item.label.trim()) return item.label;
    return '';
}

function reportKey(company, period, category, machines) {
    const selected = Array.isArray(machines)
        ? Array.from(new Set(machines.map(selectedMachineName).filter(Boolean))).sort()
        : [];
    return JSON.stringify([company, period, category, selected]);
}

function getDaily({ month, category, machines = [] }) {
    const company = JSON.parse(localStorage.getItem('user')).accountId;
    const query = 'company=' + encodeURIComponent(company)
        + (category === 99 ? '' : '&category=' + encodeURIComponent(category));
    const key = reportKey(company, month, category, machines);
    return requestReport(
        dailyCache,
        key,
        config.apiUrl + '/getMachineOEEByDate?' + query,
        { month, machine: machines },
        'day'
    );
}

function getMonthly({ year, category, machines = [] }) {
    const company = JSON.parse(localStorage.getItem('user')).accountId;
    const query = 'company=' + encodeURIComponent(company)
        + '&year=' + encodeURIComponent(year)
        + (category === 99 ? '' : '&category=' + encodeURIComponent(category));
    const key = reportKey(company, year, category, machines);
    return requestReport(
        monthlyCache,
        key,
        config.apiUrl + '/getMachineOEEByMonth?' + query,
        { machine: machines },
        'month'
    );
}

function clear() {
    dailyCache.clear();
    monthlyCache.clear();
}

export const oeeReportService = {
    getDaily,
    getMonthly,
    clear,
};
